// Node CRUD for assumption stickies. The node PUT replaces metadata wholesale,
// so every caller sends a complete metadata object (build it with
// `withDimensions` or by spreading the current metadata). A refetch of the
// canvas query is what makes the board re-render.

import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { toast } from '@/hooks/use-toast';

export function useAssumptionMutations(projectId: string) {
  const queryClient = useQueryClient();
  const [busyId, setBusyId] = useState<string | null>(null);

  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ['canvas', projectId] }),
    [queryClient, projectId],
  );

  const patchMeta = useCallback(
    async (nodeId: string, metadata: Record<string, unknown>, failMsg: string) => {
      setBusyId(nodeId);
      try {
        const res = await api.updateNode({ id: nodeId, projectId, metadata });
        if (!res.success) throw new Error(res.message || failMsg);
        await invalidate();
        return true;
      } catch (err) {
        toast({ title: failMsg, description: err instanceof Error ? err.message : '', variant: 'destructive' });
        return false;
      } finally {
        setBusyId(null);
      }
    },
    [projectId, invalidate],
  );

  const deleteAssumption = useCallback(
    async (nodeId: string) => {
      setBusyId(nodeId);
      try {
        const res = await api.deleteNode(nodeId, projectId);
        if (!res.success) throw new Error(res.message || 'Failed to delete');
        await invalidate();
        return true;
      } catch (err) {
        toast({ title: 'Delete failed', description: err instanceof Error ? err.message : '', variant: 'destructive' });
        return false;
      } finally {
        // Reset on success too: under eventual consistency the sticky can linger
        // after a delete and would otherwise stay disabled.
        setBusyId(null);
      }
    },
    [projectId, invalidate],
  );

  return { patchMeta, deleteAssumption, busyId };
}
